import React, { ReactNode, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiFileText, FiUsers, FiMessageSquare, FiUpload, FiDownload, FiMenu, FiCheck, FiLogOut, FiSettings, FiAward, FiShield, FiBriefcase, FiActivity, FiCalendar, FiPieChart, FiHeadphones, FiZap } from 'react-icons/fi';
import IconWrapper from './IconWrapper';
import { useFramework } from '../contexts/FrameworkContext';
import { useCustomer } from '../contexts/CustomerContext';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children?: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = React.useRef<HTMLDivElement>(null);
  const { framework, setFramework } = useFramework();
  const { customer, customers, setCustomer } = useCustomer();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  
  const isAdmin = user?.role === 'admin';
  
  const { logout } = useAuth();
  
  const handleLogout = () => {
    logout();
    // Redirect to landing page
    navigate('/');
  };
  
  // Close settings dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [settingsRef]);
  
  return (
    <div className="flex h-screen bg-gray-50 relative">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-secondary text-white flex flex-col transition-all duration-300 ease-in-out`}>
        {/* Logo */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="/go_logo.png" 
              alt="DataEQ Logo" 
              className={`${sidebarOpen ? 'h-10' : 'h-8'} object-contain`} 
            />
          </div>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white/70 hover:text-primary transition-colors"
          >
            <IconWrapper icon={FiMenu} />
          </button>
        </div>
        
        {/* Navigation Links */}
        <nav className="flex-1 p-4">
          <ul className="space-y-3">
            <li>
              <NavLink to="/v4/today" end className={({isActive}) =>
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiHome} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Dashboard</span>}
              </NavLink>
            </li>
            {/* <li>
              <NavLink to="/dashboard/unified-case-registry" className={({isActive}) => 
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiFileText} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Unified Case Registry (New)</span>}
              </NavLink>
            </li> */}
            <li>
              <NavLink to="/v4/today" className={({isActive}) =>
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiCalendar} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Today</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/radar" className={({isActive}) => 
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiPieChart} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Radar</span>}
              </NavLink>
            </li>

            {/* Signals Live - Production Interface */}
            <li>
              <NavLink
                to="/v4/records"
                className={({isActive}) =>
                  `flex items-center p-2.5 rounded-xl transition-all duration-200 relative ${
                    isActive
                      ? 'text-primary bg-primary/10 font-medium'
                      : 'text-white/80 hover:bg-white/5'
                  }`
              }>
                <IconWrapper icon={FiZap} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && (
                  <div className="flex items-center">
                    <span>Signals Live</span>
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-green-500 text-white rounded">Live</span>
                  </div>
                )}
              </NavLink>
            </li>

            {/* Signals - V3 Mockup Interface */}
            {/* <li>
              <NavLink 
                to="/v3/signals" 
                className={({isActive}) => 
                  `flex items-center p-2.5 rounded-xl transition-all duration-200 relative ${
                    isActive 
                      ? 'text-primary bg-primary/10 font-medium' 
                      : 'text-white/80 hover:bg-white/5'
                  }`
              }>
                <IconWrapper icon={FiZap} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && (
                  <div className="flex items-center">
                    <span>Signals (Mockup)</span>
                    <span className="ml-2 px-1.5 py-0.5 text-xs bg-gray-500 text-white rounded">Demo</span>
                  </div>
                )}
              </NavLink>
            </li> */}

            {/* Removed Explore Clusters and Root Causes nav items */}

            <li>
              <NavLink to="/v4/records" className={({isActive}) =>
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiShield} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Live Unified Case Registry</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/consumer-champion" className={({isActive}) => 
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiAward} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Consumer Champion</span>}
              </NavLink>
            </li>
            <li>
              <div className="border-b border-white/10 pb-2 mb-2">
                <div className={`text-xs text-white/50 ${sidebarOpen ? 'ml-3 mb-1.5' : 'text-center mb-1'}`}>
                  {sidebarOpen ? 'Regulatory Frameworks' : 'Reg.'}
                </div>
                {framework === 'NCD' && (
                  <NavLink 
                    to="/dashboard/consumer-understanding" 
                    className={({isActive}) => 
                      `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                        isActive 
                          ? 'text-primary bg-primary/10 font-medium' 
                          : 'text-white/80 hover:bg-white/5'
                      }`
                    }
                  >
                    <IconWrapper icon={FiUsers} className={sidebarOpen ? "mr-3" : ""} size={20} />
                    {sidebarOpen && <span>NCD Framework</span>}
                  </NavLink>
                )}
                {framework === 'TCF' && (
                  <NavLink 
                    to="/dashboard/tcf-framework" 
                    className={({isActive}) => 
                      `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                        isActive 
                          ? 'text-primary bg-primary/10 font-medium' 
                          : 'text-white/80 hover:bg-white/5'
                      }`
                    }
                  >
                    <IconWrapper icon={FiUsers} className={sidebarOpen ? "mr-3" : ""} size={20} />
                    {sidebarOpen && <span>TCF Framework</span>}
                  </NavLink>
                )}
              </div>
            </li>
            <li>
              <NavLink to="/dashboard/verbatims" className={({isActive}) => 
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiMessageSquare} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Verbatims</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/import" className={({isActive}) => 
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiUpload} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Data Import</span>}
              </NavLink>
            </li>
            <li>
              <NavLink to="/dashboard/reports" className={({isActive}) => 
                `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10 font-medium' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }>
                <IconWrapper icon={FiDownload} className={sidebarOpen ? "mr-3" : ""} size={20} />
                {sidebarOpen && <span>Reports</span>}
              </NavLink>
            </li>
            
            {/* Admin section - only visible to admins */}
            {isAdmin && (
              <>
                <li className="mt-4">
                  <div className="border-b border-white/10 pb-2 mb-2">
                    <div className={`text-xs text-white/50 ${sidebarOpen ? 'ml-3 mb-1.5' : 'text-center mb-1'}`}>
                      {sidebarOpen ? 'Administration' : 'Admin'}
                    </div>
                  </div>
                </li>
                <li>
                  <NavLink to="/dashboard/admin/users" className={({isActive}) => 
                    `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                      isActive 
                        ? 'text-primary bg-primary/10 font-medium' 
                        : 'text-white/80 hover:bg-white/5'
                    }`
                  }>
                    <IconWrapper icon={FiUsers} className={sidebarOpen ? "mr-3" : ""} size={20} />
                    {sidebarOpen && <span>Users</span>}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/dashboard/admin/organizations" className={({isActive}) => 
                    `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                      isActive 
                        ? 'text-primary bg-primary/10 font-medium' 
                        : 'text-white/80 hover:bg-white/5'
                    }`
                  }>
                    <IconWrapper icon={FiBriefcase} className={sidebarOpen ? "mr-3" : ""} size={20} />
                    {sidebarOpen && <span>Organizations</span>}
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/dashboard/admin/tasks" className={({isActive}) => 
                    `flex items-center p-2.5 rounded-xl transition-all duration-200 ${
                      isActive 
                        ? 'text-primary bg-primary/10 font-medium' 
                        : 'text-white/80 hover:bg-white/5'
                    }`
                  }>
                    <IconWrapper icon={FiActivity} className={sidebarOpen ? "mr-3" : ""} size={20} />
                    {sidebarOpen && <span>Task Monitor</span>}
                  </NavLink>
                </li>
              </>
            )}
          </ul>
        </nav>
        
        {/* User Profile */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <NavLink 
              to="/dashboard/profile"
              className={({isActive}) => 
                `flex items-center flex-1 p-2 rounded-xl transition-all duration-200 ${
                  isActive 
                    ? 'text-primary bg-primary/10' 
                    : 'text-white/80 hover:bg-white/5'
                }`
              }
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                {user?.username.charAt(0).toUpperCase() || 'U'}
              </div>
              {sidebarOpen && (
                <div className="ml-3">
                  <div className="text-sm font-medium">{user?.username || 'User'}</div>
                  <div className="text-xs text-white/50">
                    {user?.role === 'admin' ? 'Administrator' : 'User'}
                    {user?.organization_code && ` • ${user.organization_code}`}
                  </div>
                </div>
              )}
            </NavLink>
            <button 
              onClick={handleLogout}
              className="text-white/70 hover:text-primary transition-colors p-2"
              title="Logout"
            >
              <IconWrapper icon={FiLogOut} size={18} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm py-4 px-6 flex items-center justify-between hidden">
          <div>
            <h1 className="text-xl font-semibold text-secondary">
              DataEQ
            </h1>
            <p className="text-sm text-primary">
              Helping you deliver good outcomes for your customers
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button className="px-4 py-2 rounded-full text-sm text-primary border border-primary hover:bg-primary/5 transition-colors">
              Help
            </button>
            <button className="px-4 py-2 rounded-full text-sm bg-primary text-secondary hover:bg-primary/90 transition-colors">
              New Complaint
            </button>
            <div className="relative" ref={settingsRef}>
              <button 
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                onClick={() => setSettingsOpen(!settingsOpen)}
                aria-label="Settings"
              >
                <IconWrapper icon={FiSettings} size={20} />
              </button>
              
              {/* Settings Dropdown */}
              {settingsOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700">Framework Selection</h3>
                    <div className="mt-2 bg-gray-100 rounded-full p-1 flex">
                      <button 
                        className={`px-4 py-1.5 rounded-full text-sm transition-colors flex items-center ${
                          framework === 'NCD' ? 'bg-primary text-secondary' : 'text-secondary/70 hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          setFramework('NCD');
                          setSettingsOpen(false);
                        }}
                      >
                        {framework === 'NCD' && <IconWrapper icon={FiCheck} className="mr-1.5" size={12} />}
                        NCD
                      </button>
                      <button 
                        className={`px-4 py-1.5 rounded-full text-sm transition-colors flex items-center ${
                          framework === 'TCF' ? 'bg-primary text-secondary' : 'text-secondary/70 hover:bg-gray-200'
                        }`}
                        onClick={() => {
                          setFramework('TCF');
                          setSettingsOpen(false);
                        }}
                      >
                        {framework === 'TCF' && <IconWrapper icon={FiCheck} className="mr-1.5" size={12} />}
                        TCF
                      </button>
                    </div>
                  </div>
                  <div className="p-3 border-b border-gray-100">
                    <h3 className="text-sm font-medium text-gray-700">Customer</h3>
                    <div className="mt-2 space-y-2">
                      {customers.map((c) => (
                        <label key={c.id} className="flex items-center">
                          <input
                            type="radio"
                            name="customer"
                            checked={c.id === customer.id}
                            onChange={() => {
                              setCustomer(c);
                              setSettingsOpen(false);
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm">{c.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="p-2">
                    <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                      Account Settings
                    </button>
                    <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
                      Preferences
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>
        
        {/* Content */}
        <main className="flex-1 overflow-auto p-6">
          {children || <Outlet />}
        </main>
        
      </div>
    </div>
  );
};

export default Layout;
